import type { HTMLAttributes } from 'react';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Person or thing shown; used for initials, color and accessible name. */
  name: string;
  /** Optional image URL; initials are shown until it loads or if it fails. */
  src?: string;
  size?: AvatarSize;
  shape?: 'circle' | 'hexagon' | 'square';
  /** Hide from assistive tech when the name is already next to it. */
  decorative?: boolean;
}

/** First letters of the first and last word, e.g. "Maja Biene" → "MB". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

/** Stable hue index (1–6) for a name, so a person always gets the same color. */
export function hueOf(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return (Math.abs(hash) % 6) + 1;
}

export function Avatar({
  name,
  src,
  size = 'md',
  shape = 'circle',
  decorative = false,
  className,
  style,
  ...rest
}: AvatarProps) {
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img', 'aria-label': name };
  return (
    <span
      className={[styles.avatar, styles[size], styles[shape], className].filter(Boolean).join(' ')}
      style={{ background: `var(--chart-${hueOf(name)})`, ...style }}
      {...a11y}
      {...rest}
    >
      <span className={styles.initials} aria-hidden="true">
        {initials(name)}
      </span>
      {src && <img className={styles.image} src={src} alt="" />}
    </span>
  );
}
